import { ReactNode } from 'react';

import CloneExample from './steps/CloneExample';
import CreateAction from './steps/CreateAction';
import Deploying from './steps/Deploying';
import HandlingAuthentication from './steps/HandlingAuthentication';
import IndexJSFile from './steps/IndexJSFile';
import InstallCLI from './steps/InstallCLI';
import InstallNode from './steps/InstallNode';
import Introduction from './steps/Introduction';
import NextStepsSetup from './steps/NextStepsSetup';
import NextStepsTutorial from './steps/NextStepsTutorial';
import PollingTrigger from './steps/PollingTrigger';
import ProjectStructure from './steps/ProjectStructure';
import RunLogin from './steps/RunLogin';
import Testing from './steps/Testing';
import ViewLogs from './steps/ViewLogs';

export const GUIDE_URL = '/cli-guide';
export const DOCUMENTATION_URL = 'https://zapier.github.io/zapier-platform/';

export enum GUIDE_SECTIONS {
  SETUP = 'Setup',
  TUTORIAL = 'Tutorial',
}

type RootStep = {
  title: string;
  path: string;
  renderContent: () => ReactNode;
  key: string;
  nextButtonText: string;
  section: GUIDE_SECTIONS;
};

export type ParentStep = {
  title: string;
  path: string;
  key: string;
  redirectTo: string;
  section: GUIDE_SECTIONS;
};

export type ChildStep = {
  title: string;
  path: string;
  renderContent: () => ReactNode;
  key: string;
  parentKey: string;
  nextButtonText: string;
  section: GUIDE_SECTIONS;
};

export const GUIDE_STEPS: Array<RootStep | ParentStep | ChildStep> = [
  {
    title: 'Introduction',
    path: `${GUIDE_URL}/introduction`,
    renderContent: Introduction,
    key: 'intro',
    nextButtonText: 'Get Started',
    section: GUIDE_SECTIONS.SETUP,
  },
  {
    title: 'Install Node.js',
    path: `${GUIDE_URL}/install-node-js`,
    renderContent: InstallNode,
    key: 'nodejs',
    nextButtonText: 'I have configured Node.js',
    section: GUIDE_SECTIONS.SETUP,
  },
  {
    title: 'Install the Zapier CLI',
    path: `${GUIDE_URL}/install-the-zapier-cli`,
    renderContent: InstallCLI,
    key: 'install-cli',
    nextButtonText: 'I have installed the Zapier CLI',
    section: GUIDE_SECTIONS.SETUP,
  },
  {
    title: 'Run Zapier Login',
    path: `${GUIDE_URL}/run-zapier-login`,
    renderContent: RunLogin,
    key: 'run-login',
    nextButtonText: 'I have set up authentication',
    section: GUIDE_SECTIONS.SETUP,
  },
  {
    title: 'Next Steps',
    path: `${GUIDE_URL}/next-steps-setup`,
    renderContent: NextStepsSetup,
    key: 'next-setup',
    nextButtonText: 'Start the tutorial',
    section: GUIDE_SECTIONS.SETUP,
  },
  {
    title: 'Clone the Example App',
    path: `${GUIDE_URL}/clone-the-example-app`,
    renderContent: CloneExample,
    key: 'clone',
    nextButtonText: 'I have cloned the example app',
    section: GUIDE_SECTIONS.TUTORIAL,
  },
  {
    title: 'Explore the Project',
    path: `${GUIDE_URL}/project-structure`,
    key: 'explore',
    redirectTo: 'structure',
    section: GUIDE_SECTIONS.TUTORIAL,
  },
  {
    title: 'Project Structure',
    path: `${GUIDE_URL}/project-structure`,
    renderContent: ProjectStructure,
    key: 'structure',
    parentKey: 'explore',
    nextButtonText: 'Continue',
    section: GUIDE_SECTIONS.TUTORIAL,
  },
  {
    title: 'The Index.js File',
    path: `${GUIDE_URL}/the-indexjs-file`,
    renderContent: IndexJSFile,
    key: 'indexjs',
    parentKey: 'explore',
    nextButtonText: 'Continue',
    section: GUIDE_SECTIONS.TUTORIAL,
  },
  {
    title: 'A Create Action',
    path: `${GUIDE_URL}/a-create-action`,
    renderContent: CreateAction,
    key: 'action',
    parentKey: 'explore',
    nextButtonText: 'Continue',
    section: GUIDE_SECTIONS.TUTORIAL,
  },
  {
    title: 'Polling for New Data',
    path: `${GUIDE_URL}/polling-for-new-data-with-a-trigger`,
    renderContent: PollingTrigger,
    key: 'trigger',
    parentKey: 'explore',
    nextButtonText: 'Continue',
    section: GUIDE_SECTIONS.TUTORIAL,
  },
  {
    title: 'Handling Authentication',
    path: `${GUIDE_URL}/handling-authentication`,
    renderContent: HandlingAuthentication,
    key: 'auth',
    parentKey: 'explore',
    nextButtonText: 'I have explored the project',
    section: GUIDE_SECTIONS.TUTORIAL,
  },
  {
    title: 'Testing Your App',
    path: `${GUIDE_URL}/testing-your-app`,
    renderContent: Testing,
    key: 'test',
    nextButtonText: 'I have tested the app',
    section: GUIDE_SECTIONS.TUTORIAL,
  },
  {
    title: 'Deploying Your App',
    path: `${GUIDE_URL}/deploying-your-app`,
    renderContent: Deploying,
    key: 'deploy',
    nextButtonText: 'I have deployed my app',
    section: GUIDE_SECTIONS.TUTORIAL,
  },
  {
    title: 'View Logs',
    path: `${GUIDE_URL}/view-logs`,
    renderContent: ViewLogs,
    key: 'logs',
    nextButtonText: 'I have viewed my logs',
    section: GUIDE_SECTIONS.TUTORIAL,
  },
  {
    title: 'Next Steps',
    path: `${GUIDE_URL}/next-steps-tutorial`,
    renderContent: NextStepsTutorial,
    key: 'next-tutorial',
    nextButtonText: 'Take me to the docs',
    section: GUIDE_SECTIONS.TUTORIAL,
  },
];
